import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Star, Flag, RotateCcw, Archive, Trash2, BookOpen, FileText, Link2, Users, Check, Pencil } from "lucide-react";
import api, { apiErr, fmtMoney } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtRange, STATUS_LABEL } from "../components/TripSwitcher";
import { Drawer, DrawerContent, DrawerTitle } from "../components/ui/drawer";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function TripDetail() {
  const { id } = useParams();
  const { org, config, isOwner, setViewTrip, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [team, setTeam] = useState([]);
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(null); // "make-current" | "end" | "delete" | "archive"
  const [edit, setEdit] = useState(false);
  const [reportUrl, setReportUrl] = useState("");

  const load = async () => {
    try {
      const [a, b] = await Promise.all([api.get(`/trips/${id}`), api.get(`/trips/${id}/team`)]);
      setT(a.data); setTeam(b.data);
    } catch (e) { toast.error(apiErr(e)); navigate("/app/trips"); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (path, okMsg, after, body) => {
    setBusy(path);
    try {
      await api.post(`/trips/${id}/${path}`, body);
      toast.success(okMsg);
      setConfirm(null);
      if (after) return after();
      await refreshMe(); await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(""); }
  };
  const del = async () => {
    setBusy("delete");
    try { await api.delete(`/trips/${id}`); toast.success("Journey deleted"); navigate("/app/trips"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const toggleTeam = async (m) => {
    try {
      await api.post(`/trips/${id}/team`, { user_id: m.id, on: !m.on_trip });
      setTeam((ts) => ts.map((x) => (x.id === m.id ? { ...x, on_trip: !x.on_trip } : x)));
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!t) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin accent-text" /></div>;
  const s = t.stats || {};
  const isCurrent = t.status === "current";
  const isViewing = config?.id === t.id;
  const publicLink = `${window.location.origin}/j/${org?.slug}${isCurrent ? "" : `?trip=${t.public_slug}`}`;
  const endPassed = t.end_date && t.end_date < new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-5" data-testid="trip-detail-page">
      <div className="flex items-center gap-3">
        <button data-testid="td-back" onClick={() => navigate("/app/trips")} className="text-[var(--brand-navy)]"><ArrowLeft className="h-5 w-5" /></button>
        <div className="min-w-0">
          <h1 className="font-heading font-extrabold text-2xl text-[var(--brand-navy)] truncate">{t.trip_name}</h1>
          <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-[#0d2340]/5 text-[#0d2340]" data-testid="td-status">{STATUS_LABEL[t.status]}{t.status === "planning" ? " · hidden from partners" : ""}</span>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-5 shadow-[0_4px_24px_rgba(44,44,62,0.04)]">
        <div className="flex items-start justify-between gap-2">
          <div className="text-sm text-muted-foreground">{fmtRange(t.start_date, t.end_date)}{t.origin_country ? ` · from ${t.origin_country}` : ""}</div>
          <button data-testid="td-edit-btn" onClick={() => setEdit(true)} className="text-xs font-semibold accent-text flex items-center gap-1"><Pencil className="h-3 w-3" /> Edit</button>
        </div>
        {t.report?.external_url && <a data-testid="td-report-link" href={t.report.external_url} target="_blank" rel="noreferrer" className="text-xs accent-text font-semibold mt-1 inline-block truncate max-w-full">Report link: {t.report.external_url} ↗</a>}
        <div className="text-sm mt-1">Goal <b>{fmtMoney(t.budget_goal_cents || 0, t.currency)}</b>{s.countries?.length ? <> · {s.countries.length} {s.countries.length === 1 ? "country" : "countries"}: {s.countries.join(", ")}</> : " · no stops yet"}</div>
        <div className="grid grid-cols-4 gap-1 mt-4 text-center">
          {[["Dispatches", s.dispatches], ["Prayers", s.prayers], ["Givers", s.givers], ["Raised", fmtMoney(s.raised_cents || 0, t.currency)]].map(([l, n]) => (
            <div key={l}><div className="font-heading font-bold text-lg text-[#0d2340]">{n ?? 0}</div><div className="text-[10px] text-muted-foreground">{l}</div></div>
          ))}
        </div>
      </div>

      {/* Primary actions by status */}
      <div className="space-y-2">
        {!isCurrent && t.status !== "past" && (
          <Btn testid="td-make-current" onClick={() => setConfirm("make-current")} primary><Star className="h-4 w-4" /> Make this the current journey</Btn>
        )}
        {isCurrent && (
          <Btn testid="td-end-trip" onClick={() => setConfirm("end")} primary={!!endPassed}><Flag className="h-4 w-4" /> End journey{endPassed ? " — dates have passed" : ""}</Btn>
        )}
        {t.status === "completed" && <Btn testid="td-reopen" onClick={() => act("reopen", "Journey reopened")}><RotateCcw className="h-4 w-4" /> Reopen (undo end)</Btn>}
        {t.status === "past" && isOwner && <Btn testid="td-restore" onClick={() => act("restore", "Back to Planning — you can edit it or make it current")}><RotateCcw className="h-4 w-4" /> Restore to Planning</Btn>}
        {!isViewing && <Btn testid="td-open" onClick={() => setViewTrip(t.id, org?.current_trip_id)}><BookOpen className="h-4 w-4" /> {t.status === "past" ? "Look back at this journey" : "Preview in the app"}</Btn>}
        {t.status !== "planning" && <Btn testid="td-report" onClick={() => navigate(`/app/trips/${t.id}/report`)}><FileText className="h-4 w-4" /> Trip report</Btn>}
        {org?.public_feed_enabled && t.status !== "planning" && (
          <Btn testid="td-copy-link" onClick={() => { navigator.clipboard.writeText(publicLink); toast.success("Public link copied"); }}><Link2 className="h-4 w-4" /> Copy public link</Btn>
        )}
        <div className="flex gap-2 pt-1">
          {(t.status === "completed" || t.status === "planning") && t.id !== org?.current_trip_id && <button data-testid="td-archive" onClick={() => setConfirm("archive")} className="flex-1 text-xs text-muted-foreground py-2 flex items-center justify-center gap-1"><Archive className="h-3.5 w-3.5" /> Archive</button>}
          {t.status === "planning" && isOwner && <button data-testid="td-delete" onClick={() => setConfirm("delete")} className="flex-1 text-xs text-destructive py-2 flex items-center justify-center gap-1"><Trash2 className="h-3.5 w-3.5" /> Delete</button>}
        </div>
      </div>

      {/* Team roster for this journey */}
      <div className="bg-white rounded-2xl p-5 shadow-[0_4px_24px_rgba(44,44,62,0.04)]" data-testid="td-team">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#0d2340]"><Users className="h-4 w-4 accent-text" /> Who's going</div>
        <p className="text-xs text-muted-foreground mt-0.5">Team members shown on this journey's "Meet the Team".</p>
        <div className="mt-3 space-y-1.5">
          {team.length === 0 && <p className="text-xs text-muted-foreground">No team members yet — add them under Our Team.</p>}
          {team.map((m) => (
            <button key={m.id} data-testid={`td-team-${m.id}`} onClick={() => toggleTeam(m)} className={`w-full flex items-center gap-3 rounded-xl px-3 py-2 text-left border ${m.on_trip ? "border-[#C9973A]/50 bg-[#C9973A]/5" : "border-transparent bg-muted/40"}`}>
              <span className={`h-5 w-5 rounded-full flex items-center justify-center ${m.on_trip ? "accent-bg text-white" : "bg-muted"}`}>{m.on_trip && <Check className="h-3 w-3" />}</span>
              <span className="flex-1 text-sm"><span className="font-medium">{m.name}</span>{m.title && <span className="text-muted-foreground"> · {m.title}</span>}</span>
            </button>
          ))}
        </div>
      </div>

      <ConfirmSheet open={confirm === "make-current"} onClose={() => setConfirm(null)} title={`Switch to ${t.trip_name}?`} busy={busy === "make-current"}
        cta="Make current & announce" onConfirm={() => act("make-current", "This is now the current journey", () => setViewTrip(null))}
        points={[
          "✅ Partners see this journey the next time they open the app",
          `📚 ${config?.trip_name && config.id !== t.id ? config.trip_name : "The current journey"} moves to Past journeys — read-only, nothing deleted`,
          "🙏 Partners' pray / give / go choices carry over; they'll be gently asked to confirm",
          "🔔 Everyone gets one push + email: “A new journey begins…”",
        ]} />
      <ConfirmSheet open={confirm === "end"} onClose={() => setConfirm(null)} title={`End ${t.trip_name}?`} busy={busy === "end"} cta="End journey" onConfirm={() => act("end", "Journey marked complete", null, { report_url: reportUrl })}
        points={["🏁 Marks the journey Completed — partners get a thank-you card with the trip report", "✍️ You can still post dispatches, thank-yous and edit the in-app report", "📖 It stays what partners see until you make the next journey current", "↩ You can reopen it anytime"]}>
        <div className="mt-4">
          <Label className="text-xs">Link to your own report <span className="text-muted-foreground">(optional)</span></Label>
          <Input data-testid="end-report-url" value={reportUrl} onChange={(e) => setReportUrl(e.target.value)} placeholder="https://akop.live/report" className="mt-1 rounded-xl" />
          <p className="text-[11px] text-muted-foreground mt-1">Partners will see “Read the full report” pointing here, alongside the in-app summary.</p>
        </div>
      </ConfirmSheet>
      <ConfirmSheet open={confirm === "archive"} onClose={() => setConfirm(null)} title={`Archive ${t.trip_name}?`} busy={busy === "archive"} cta="Archive" onConfirm={() => act("archive", "Journey archived")}
        points={["📚 Moves to Past journeys, read-only", "🔒 No more posts or edits", "↩ The owner can restore it to Planning later"]} />
      <ConfirmSheet open={confirm === "delete"} onClose={() => setConfirm(null)} title={`Delete ${t.trip_name}?`} busy={busy === "delete"} cta="Delete permanently" danger onConfirm={del}
        points={["🗑 Removes this planning journey and anything created inside it", "This cannot be undone"]} />
      {edit && <EditSheet t={t} onClose={() => setEdit(false)} onSaved={async (u) => { setT({ ...t, ...u }); setEdit(false); if (isViewing) await refreshMe(); }} />}
    </div>
  );
}

function Btn({ children, onClick, primary, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`w-full rounded-full h-12 flex items-center justify-center gap-2 font-semibold text-sm transition-transform active:scale-[0.98] ${primary ? "accent-bg text-white shadow-md" : "border border-[#0d2340]/15 text-[#0d2340] bg-white"}`}>{children}</button>
  );
}

function ConfirmSheet({ open, onClose, title, points, cta, onConfirm, busy, danger, children }) {
  return (
    <Drawer open={open} onOpenChange={(o) => !o && onClose()}>
      <DrawerContent className="rounded-t-[28px] px-5 pb-8" data-testid="trip-confirm-sheet">
        <div className="mx-auto w-full max-w-md">
          <DrawerTitle className="font-heading font-bold text-xl text-[#0d2340] mt-3">{title}</DrawerTitle>
          <ul className="mt-3 text-sm space-y-2 text-[#2C2C3E]">{points.map((p) => <li key={p}>{p}</li>)}</ul>
          {children}
          <button data-testid="trip-confirm-btn" disabled={busy} onClick={onConfirm} className={`mt-5 w-full rounded-full h-12 font-semibold text-white ${danger ? "bg-destructive" : "accent-bg"}`}>{busy ? "Working…" : cta}</button>
          <button data-testid="trip-confirm-cancel" onClick={onClose} className="w-full text-center text-sm text-muted-foreground py-3">Not yet</button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

function EditSheet({ t, onClose, onSaved }) {
  const [f, setF] = useState({ trip_name: t.trip_name || "", start_date: t.start_date || "", end_date: t.end_date || "", budget_goal: t.budget_goal_cents ? t.budget_goal_cents / 100 : "", currency: t.currency || "USD", report_url: t.report?.external_url || "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/trips/${t.id}`, { trip_name: f.trip_name || null, start_date: f.start_date || null, end_date: f.end_date || null, currency: f.currency || null, budget_goal_cents: f.budget_goal === "" ? null : Math.round(parseFloat(f.budget_goal) * 100) });
      const rep = await api.put(`/trips/${t.id}/report`, { external_url: f.report_url });
      toast.success("Saved"); onSaved({ ...data, report: rep.data });
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <DrawerContent className="rounded-t-[28px] px-5 pb-8" data-testid="trip-edit-sheet">
        <div className="mx-auto w-full max-w-md space-y-3 mt-3">
          <DrawerTitle className="font-heading font-bold text-xl text-[#0d2340]">Edit journey</DrawerTitle>
          <div><Label className="text-xs">Name</Label><Input data-testid="te-name" value={f.trip_name} onChange={(e) => setF({ ...f, trip_name: e.target.value })} className="mt-1 rounded-xl" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Departs</Label><Input data-testid="te-start" type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className="mt-1 rounded-xl" /></div>
            <div><Label className="text-xs">Returns</Label><Input data-testid="te-end" type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} className="mt-1 rounded-xl" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Currency</Label><Input data-testid="te-currency" value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })} className="mt-1 rounded-xl" /></div>
            <div><Label className="text-xs">Budget goal</Label><Input data-testid="te-goal" type="number" value={f.budget_goal} onChange={(e) => setF({ ...f, budget_goal: e.target.value })} className="mt-1 rounded-xl" /></div>
          </div>
          <div><Label className="text-xs">Report link <span className="text-muted-foreground">(your website, optional)</span></Label><Input data-testid="te-report-url" value={f.report_url} onChange={(e) => setF({ ...f, report_url: e.target.value })} placeholder="https://akop.live/report" className="mt-1 rounded-xl" /></div>
          <button data-testid="te-save" disabled={saving} onClick={save} className="w-full rounded-full h-12 accent-bg text-white font-semibold mt-2">{saving ? "Saving…" : "Save"}</button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
